import { inject, injectable } from 'inversify';
import { action, computed, makeObservable } from 'mobx';
import type { Transparency } from '@/engine/app/interfaces';
import type { IObjectViewModel } from '@/engine/app/interfaces';
import type { ViewOption, ViewOptionsGroup, ViewOptionsStruct } from '@/engine/app/interfaces';
import type { ViewsService } from '@/engine/app/Views.service';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { TYPES } from '@/engine/ioc';
import type { ObjectColor } from './ColorMarker';
import { type WithMixed, type WithUndefined } from './types';
import { viewOptionsStructureManager } from './viewOptionsStructure';
import type { ViewOptionsCommandFactory } from './commands/ViewOptionsCommand.factory';
import type { CommandProcessor } from '@/engine/app/modules/core/services/CommandProcessor';
import { CORE_TYPES } from '@/engine/app/modules/core';

@injectable()
export class ViewOptionsController {
  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _commandFactory: ViewOptionsCommandFactory;

  private readonly _commandProcessor: CommandProcessor;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.ViewOptionsCommandFactory) commandFactory: ViewOptionsCommandFactory,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: CommandProcessor
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._commandFactory = commandFactory;
    this._commandProcessor = commandProcessor;

    makeObservable(this);
  }

  @computed
  public get objects(): Array<IObjectViewModel> | undefined {
    return this._viewsService.currentView?.getObjects();
  }

  @computed
  public get viewOptions(): ViewOptionsStruct | undefined {
    return this._viewsService.currentView?.getViewOptions();
  }

  public isObjectAvailable(viewOption: ViewOption): boolean {
    const isObjectAvailable = viewOption.descriptors.some((descriptor) => {
      const model = this._planningAlgorithm.getValue(descriptor, DT.Mesh);
      return !!model?.value.index;
    });

    return isObjectAvailable;
  }

  /** only in some edge cases mapping descriptors will exist */
  private _getMappedDescriptors(viewOption: ViewOption): Array<string> {
    return viewOption.descriptors.map((descriptor) => {
      const foundItem = this._viewsService.currentView?.descriptorsMapping?.find(
        (item) => item.targetDescriptor === descriptor
      );

      return foundItem?.sourceDescriptor || descriptor;
    });
  }

  private _getPropertyValue<T extends IObjectViewModel[keyof IObjectViewModel]>(
    viewOption: ViewOption,
    property: keyof IObjectViewModel
  ): T | undefined {
    const obj = this.objects?.find((obj: IObjectViewModel) => viewOption.descriptors.includes(obj.descriptor));
    if (obj === undefined) {
      throw new Error(
        `no object descriptor found matching viewOption ${viewOption.descriptors?.at(0) ?? viewOption.label}`
      );
    }
    let isVisible = obj[property] as T | undefined;

    this.objects
      ?.filter((obj: IObjectViewModel) => viewOption.descriptors.includes(obj.descriptor))
      .forEach((object) => {
        if (isVisible !== object[property]) {
          isVisible = undefined;
        }
      });
    return isVisible;
  }

  private _getGroupPropertyValue<T extends IObjectViewModel[keyof IObjectViewModel]>(
    viewOptionsGroup: ViewOptionsGroup,
    callbackFn: (ViewOption: ViewOption) => WithMixed<WithUndefined<T>>
  ): WithMixed<WithUndefined<T>> {
    const value = viewOptionsStructureManager.checkGroupProperty(viewOptionsGroup, callbackFn);
    if (value === 'empty') {
      throw new Error(`empty group property check for ${viewOptionsGroup.label}`);
    }
    return value;
  }

  public getViewOptionByDescriptor(descriptor: string | undefined): ViewOption | undefined {
    return descriptor ? this._viewsService.currentView?.descriptor2ViewOption?.[descriptor] : undefined;
  }

  public isVisible(viewOption: ViewOption): WithUndefined<boolean> {
    return this._getPropertyValue(viewOption, 'isVisible');
  }

  public transparency(viewOption: ViewOption): WithUndefined<Transparency> {
    return this._getPropertyValue(viewOption, 'transparency');
  }

  public isContourVisible(viewOption: ViewOption): WithUndefined<boolean> {
    return this._getPropertyValue(viewOption, 'isContourVisible');
  }

  public isGroupVisible(item: ViewOptionsGroup): WithMixed<WithUndefined<boolean>> {
    return this._getGroupPropertyValue(item, this.isVisible.bind(this));
  }

  public isGroupContourVisible(item: ViewOptionsGroup): WithMixed<WithUndefined<boolean>> {
    return this._getGroupPropertyValue(item, this.isContourVisible.bind(this));
  }

  public groupTransparency(item: ViewOptionsGroup): WithMixed<WithUndefined<Transparency>> {
    return this._getGroupPropertyValue(item, this.transparency.bind(this));
  }

  public color(viewOption: ViewOption): ObjectColor {
    const _isHashedString = (input: string): input is `#${string}` => input.startsWith('#');

    const descriptors = this._getMappedDescriptors(viewOption);
    const obj = this.objects?.filter((obj: IObjectViewModel) => descriptors.includes(obj.descriptor))[0];

    if (obj?.color === undefined || !_isHashedString(obj.color)) {
      throw new Error('Object color is undefined or have wrong format');
    }
    if (obj?.isVertexColoring === true) {
      return 'Vertex';
    }
    return obj.color;
  }

  public getLabel(viewOption: ViewOption): string {
    const descriptors = this._getMappedDescriptors(viewOption);
    const obj = this.objects?.filter((obj: IObjectViewModel) => descriptors.includes(obj.descriptor))[0];
    return obj?.label ?? '';
  }

  @action
  public setTransparency(viewOption: ViewOption, transparency: Transparency): void {
    const command = this._commandFactory.createSetViewOptionTransparencyCommand(viewOption, transparency);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.execute(command);
  }

  @action
  public setVisibility(viewOption: ViewOption, isVisible: boolean): void {
    const command = this._commandFactory.createSetViewOptionVisibilityCommand(viewOption, isVisible);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.execute(command);
  }

  @action
  public setContourVisibility(viewOption: ViewOption, isVisible: boolean): void {
    const command = this._commandFactory.createSetViewOptionContourVisibilityCommand(viewOption, isVisible);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.execute(command);
  }

  @action
  public setGroupVisibility(item: ViewOptionsGroup, isVisible: boolean): void {
    const command = this._commandFactory.createSetGroupVisibilityCommand(item, isVisible);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.execute(command);
  }

  @action
  public setGroupContourVisibility(item: ViewOptionsGroup, isVisible: boolean): void {
    const command = this._commandFactory.createSetGroupContourVisibilityCommand(item, isVisible);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.execute(command);
  }

  @action
  public setGroupTransparency(item: ViewOptionsGroup, transparency: Transparency): void {
    const command = this._commandFactory.createSetGroupTransparencyCommand(item, transparency);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.execute(command);
  }
}
